import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExtensionIcon from '@mui/icons-material/Extension';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

type ExtensionStatus = 'active' | 'inactive' | 'checking';

type ExtensionStatusButtonProps = {
  extensionKey: string;
  source: string;
  label: string;
  storeUrl?: string;
};

function markerSelector(extensionKey: string, source: string) {
  return `[data-extension="${extensionKey}"], [data-source="${source}"]`;
}

const ExtensionStatusButton: React.FC<ExtensionStatusButtonProps> = ({
  extensionKey,
  source,
  label,
  storeUrl,
}) => {
  const [status, setStatus] = useState<ExtensionStatus>('checking');
  const [version, setVersion] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const readMarker = useCallback(() => {
    const marker = document.querySelector(markerSelector(extensionKey, source));
    if (!marker) return false;
    setVersion(marker.getAttribute('data-version') || '');
    setStatus('active');
    return true;
  }, [extensionKey, source]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.source === source || data.type === source) {
        if (typeof data.version === 'string') setVersion(data.version);
        setStatus('active');
      }
    };

    window.addEventListener('message', onMessage);
    if (!readMarker()) {
      const timeout = window.setTimeout(() => {
        if (!readMarker()) setStatus('inactive');
      }, 1500);
      const interval = window.setInterval(readMarker, 5000);
      return () => {
        window.removeEventListener('message', onMessage);
        window.clearTimeout(timeout);
        window.clearInterval(interval);
      };
    }

    const interval = window.setInterval(readMarker, 5000);
    return () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(interval);
    };
  }, [readMarker, source]);

  const tooltip =
    status === 'active'
      ? `${label} is installed${version ? ` (v${version})` : ''}`
      : status === 'inactive'
      ? `${label} is not installed. Click to install.`
      : `Checking for ${label}…`;

  const handleClick = () => {
    if (status === 'active' || readMarker()) {
      return;
    }
    setStatus('inactive');
    setDialogOpen(true);
  };

  return (
    <>
      <Tooltip title={tooltip}>
        <IconButton
          size="large"
          aria-label={tooltip}
          color="inherit"
          onClick={handleClick}
          sx={{
            position: 'relative',
            ...(status === 'active' && {
              color: '#4caf50',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '6px',
                right: '6px',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#4caf50',
                border: '2px solid white',
              },
            }),
            ...(status === 'inactive' && { opacity: 0.35 }),
            ...(status === 'checking' && {
              opacity: 0.6,
              animation: 'extensionPulse 2s infinite',
              '@keyframes extensionPulse': {
                '0%': { opacity: 0.6 },
                '50%': { opacity: 0.9 },
                '100%': { opacity: 0.6 },
              },
            }),
          }}
        >
          <ExtensionIcon />
        </IconButton>
      </Tooltip>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby={`${extensionKey}-install-title`}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id={`${extensionKey}-install-title`}>{label} not found</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {storeUrl
              ? `${label} is not installed or not enabled. Install it from the Chrome Web Store, then refresh this page.`
              : `${label} is not installed or not enabled. Load the unpacked extension in Chrome, then refresh this page.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
          {storeUrl ? (
            <Button
              variant="contained"
              startIcon={<OpenInNewIcon />}
              onClick={() => {
                window.open(storeUrl, '_blank', 'noopener,noreferrer');
                setDialogOpen(false);
              }}
            >
              Install extension
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExtensionStatusButton;
